'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

type FieldElement = HTMLInputElement | HTMLTextAreaElement

type Props =
  | ({ multiline?: false } & React.InputHTMLAttributes<HTMLInputElement>)
  | ({ multiline: true } & React.TextareaHTMLAttributes<HTMLTextAreaElement>)

/** Styles the mirror must copy so its text wraps and measures exactly like the field's. */
const MIRRORED = [
  'boxSizing',
  'width',
  'borderLeftWidth',
  'borderRightWidth',
  'borderTopWidth',
  'borderBottomWidth',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'paddingBottom',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'textTransform',
  'textIndent',
  'wordSpacing',
  'tabSize',
] as const

interface Caret {
  x: number
  y: number
  h: number
}

/**
 * A text field whose caret is a glowing brand-cyan bar that glides as you type. The native
 * caret is hidden and a drawn one is positioned via a hidden "mirror" copy of the text up to the
 * cursor. While text is selected the drawn caret hides and the native selection shows instead.
 */
export default function GlowCaretField({
  className = '',
  style,
  ...props
}: Props & { className?: string }) {
  const fieldRef = useRef<FieldElement | null>(null)
  const mirrorRef = useRef<HTMLDivElement | null>(null)
  const caretRef = useRef<HTMLSpanElement | null>(null)
  const [caret, setCaret] = useState<Caret | null>(null)
  // Bumped on every move so the blink restarts: the caret stays solid while typing, like a native one.
  const [moves, setMoves] = useState(0)

  const measure = useCallback(() => {
    const field = fieldRef.current
    if (!field || document.activeElement !== field) return setCaret(null)
    const { selectionStart, selectionEnd } = field
    if (selectionStart === null || selectionStart !== selectionEnd) return setCaret(null)

    const cs = getComputedStyle(field)
    let mirror = mirrorRef.current
    if (!mirror) {
      mirror = document.createElement('div')
      mirror.setAttribute('aria-hidden', 'true')
      Object.assign(mirror.style, {
        position: 'absolute',
        visibility: 'hidden',
        top: '0',
        left: '-9999px',
        overflow: 'hidden',
      })
      document.body.appendChild(mirror)
      mirrorRef.current = mirror
    }
    MIRRORED.forEach((k) => (mirror.style[k] = cs[k]))
    const multiline = field instanceof HTMLTextAreaElement
    mirror.style.whiteSpace = multiline ? 'pre-wrap' : 'pre'
    mirror.style.overflowWrap = multiline ? 'break-word' : 'normal'
    mirror.style.height = 'auto'

    mirror.textContent = field.value.slice(0, selectionStart)
    const marker = document.createElement('span')
    marker.textContent = '\u200b' // zero-width: gives the marker a line box without adding width
    mirror.appendChild(marker)

    const lineHeight = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.25
    const h = Math.round(parseFloat(cs.fontSize) * 1.25)
    setCaret({
      x: field.offsetLeft + marker.offsetLeft - field.scrollLeft,
      // inputs centre their single line vertically; textareas stack lines from the top padding
      y: multiline
        ? field.offsetTop + marker.offsetTop - field.scrollTop + (lineHeight - h) / 2
        : field.offsetTop + (field.offsetHeight - h) / 2,
      h,
    })
    setMoves((n) => n + 1)
  }, [])

  useEffect(() => {
    const field = fieldRef.current
    if (!field) return
    // Measure on the next frame, never inside the event: these native listeners run before React's
    // own onChange, and a synchronous state update here re-renders the controlled field with its old
    // value, which throws away the keystroke.
    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    const events = ['input', 'focus', 'blur', 'keyup', 'mouseup', 'scroll', 'select'] as const
    const onSelection = () => document.activeElement === field && schedule()
    events.forEach((e) => field.addEventListener(e, schedule))
    document.addEventListener('selectionchange', onSelection)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      events.forEach((e) => field.removeEventListener(e, schedule))
      document.removeEventListener('selectionchange', onSelection)
      window.removeEventListener('resize', schedule)
      mirrorRef.current?.remove()
      mirrorRef.current = null
    }
  }, [measure])

  // Rewind the blink on every move (without remounting, which would drop the glide transition).
  useEffect(() => {
    caretRef.current?.getAnimations().forEach((a) => {
      if (a instanceof CSSAnimation) a.currentTime = 0
    })
  }, [moves])

  // Controlled value changes (e.g. the form resetting) move the cursor without an input event.
  useLayoutEffect(measure, [props.value, measure])

  const fieldProps = {
    className,
    // transparent native caret; the drawn one below replaces it
    style: { ...style, caretColor: 'transparent' },
  }

  return (
    <div className="relative">
      {props.multiline ? (
        <textarea
          {...(({ multiline: _, ...rest }) => rest)(props)}
          {...fieldProps}
          ref={(el) => {
            fieldRef.current = el
          }}
        />
      ) : (
        <input
          {...(({ multiline: _, ...rest }) => rest)(props)}
          {...fieldProps}
          ref={(el) => {
            fieldRef.current = el
          }}
        />
      )}
      {caret && (
        <span
          ref={caretRef}
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-0 w-[2px] rounded-full motion-safe:animate-[caret-blink_1.06s_steps(1)_0.5s_infinite] motion-safe:transition-transform motion-safe:duration-75 motion-safe:ease-out"
          style={{
            height: caret.h,
            transform: `translate(${caret.x}px, ${caret.y}px)`,
            background: 'var(--flagship-cyan)',
            boxShadow: '0 0 6px 1px rgba(34,211,238,0.9), 0 0 14px 2px rgba(34,211,238,0.45)',
          }}
        />
      )}
    </div>
  )
}
